import { Image } from "@-/dev.improve.haniverse.components.ui.image";
import { PostHogMaskView } from "@-/com.npmjs.posthog-react-native";
import { View, type TextLayoutEvent } from "@-/com.npmjs.react-native";
import { withUniwind } from "@-/com.npmjs.uniwind";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useCallback, useState } from "react";

/** The avatar is the one user-supplied image in the app: always masked in session replays. */
const UniwindPostHogMaskView = withUniwind(PostHogMaskView);

export function ProfileAvatar({
  card = false,
  initialClassName,
  shape = "circle",
  user,
}: {
  /** A card shows the whole photo, with square edges and a larger fallback initial. */
  card?: boolean;
  /** The fallback initial's size, for an avatar much larger than a list row's (`text-5xl`). */
  initialClassName?: string;
  shape?: "circle" | "square";
  user: { email?: string | null; image?: string | null; name: string } | null;
}) {
  const [failedImage, setFailedImage] = useState<string | null>(null);
  const [initialOffset, setInitialOffset] = useState(0);
  const centerInitial = useCallback((event: TextLayoutEvent) => {
    const line = event.nativeEvent.lines[0];
    if (line === undefined) {
      return;
    }
    // Center the capital's visible ink, rather than the font's asymmetric ascent/descent box.
    // Native metrics follow the resolved font and size, including large quest-call avatars.
    setInitialOffset(line.height / 2 - (line.ascender - line.capHeight / 2));
  }, []);
  const uri = user?.image;
  return (
    <UniwindPostHogMaskView className="size-full">
      {uri && uri !== failedImage ? (
        <Image
          accessibilityIgnoresInvertColors
          className={card || shape === "square" ? "size-full rounded-xs" : "size-full rounded-full"}
          contentFit={card ? "contain" : "cover"}
          onError={() => setFailedImage(uri)}
          source={{ uri }}
        />
      ) : (
        <View
          className={`size-full items-center justify-center ${card || shape === "square" ? "rounded-xs" : "rounded-full"} ${user ? "bg-primary" : "bg-background"}`}
        >
          {user ? (
            <Text
              className={`${initialClassName ?? (card ? "text-5xl" : "text-lg")} text-center font-semibold text-primary-foreground`}
              onTextLayout={centerInitial}
              style={{ transform: [{ translateY: initialOffset }] }}
            >
              {(user.name.trim() || user.email?.trim() || "U").charAt(0).toUpperCase()}
            </Text>
          ) : (
            <Icon className="text-muted-foreground" name="MdPersonOutline" size={card ? 48 : 24} />
          )}
        </View>
      )}
    </UniwindPostHogMaskView>
  );
}
